/**
 * AddNodeMenu — context popover that lists the node types you can add at the
 * current cursor / output handle.
 *
 * Two entry points (both wired in Canvas + FlowNode):
 *   - Right-click empty canvas → all addable node types
 *   - Click "+" on an output handle → only types that form a valid connection
 *     from THAT output (filtered via connectionValidator)
 *
 * Selecting an item:
 *   - Creates the node at the click position (or just under the source node)
 *   - Auto-connects from the source handle if there's one
 *   - Closes the menu
 */

import { useEffect, useMemo, useRef } from "react";
import { useReactFlow } from "@xyflow/react";
import {
  Zap,
  GitBranch,
  Split,
  FlaskConical,
  Clock,
  ShoppingCart,
  Lock,
  Timer,
  Shield,
  Webhook,
  type LucideIcon,
} from "lucide-react";
import { clsx } from "clsx";
import type { NodeType } from "../../types/flow";
import type { CategoryKey } from "./nodes/CardShell";
import { useEditorStore } from "./editorStore";
import {
  ADDABLE_NODE_TYPES,
  validTargetsForOutput,
} from "./connectionValidator";

interface CatalogEntry {
  type: NodeType;
  label: string;
  description: string;
  icon: LucideIcon;
  category: CategoryKey;
}

const CATALOG: Record<NodeType, CatalogEntry> = {
  trigger: {
    type: "trigger",
    label: "Trigger",
    description: "Start of the flow.",
    icon: Zap,
    category: "trigger",
  },
  condition: {
    type: "condition",
    label: "Condition",
    description: "Two-branch decision on a JSONLogic rule.",
    icon: GitBranch,
    category: "routing",
  },
  switch: {
    type: "switch",
    label: "Switch",
    description: "N-branch match on an expression.",
    icon: Split,
    category: "routing",
  },
  ab_test: {
    type: "ab_test",
    label: "A/B test",
    description: "Weighted random split, sticky per session.",
    icon: FlaskConical,
    category: "routing",
  },
  schedule: {
    type: "schedule",
    label: "Schedule",
    description: "Date-range window with optional days-of-week.",
    icon: Clock,
    category: "routing",
  },
  checkout_page: {
    type: "checkout_page",
    label: "Checkout page",
    description: "Binds the session to a project + theme + layout.",
    icon: ShoppingCart,
    category: "terminal",
  },
  capture_policy: {
    type: "capture_policy",
    label: "Capture policy",
    description: "Auto / manual_on_fulfillment / manual_explicit.",
    icon: Lock,
    category: "postpurchase",
  },
  delay: {
    type: "delay",
    label: "Delay",
    description: "Wait a fixed or context-driven duration.",
    icon: Timer,
    category: "postpurchase",
  },
  fraud_check: {
    type: "fraud_check",
    label: "Fraud check",
    description: "3-band result from Cost+ / Shopify / Sift / Signifyd.",
    icon: Shield,
    category: "postpurchase",
  },
  http_request: {
    type: "http_request",
    label: "HTTP request",
    description: "Fire a webhook / API call from inside the flow.",
    icon: Webhook,
    category: "action",
  },
};

const CATEGORY_DOT: Record<CategoryKey, string> = {
  trigger: "bg-category-trigger",
  routing: "bg-category-routing",
  terminal: "bg-category-terminal",
  postpurchase: "bg-category-postpurchase",
  action: "bg-category-action",
  error: "bg-category-error",
};

const CATEGORY_LABEL: Record<CategoryKey, string> = {
  trigger: "Triggers",
  routing: "Routing",
  terminal: "Terminal",
  postpurchase: "Post-purchase",
  action: "Actions",
  error: "Error",
};

const CATEGORY_ORDER: CategoryKey[] = [
  "routing",
  "terminal",
  "postpurchase",
  "action",
];

const MENU_WIDTH = 280;
const MENU_MAX_HEIGHT = 420;

export function AddNodeMenu() {
  const addMenu = useEditorStore((s) => s.addMenu);
  const closeAddMenu = useEditorStore((s) => s.closeAddMenu);
  const definition = useEditorStore((s) => s.definition);
  const positions = useEditorStore((s) => s.positions);
  const addNode = useEditorStore((s) => s.addNode);
  const connectEdge = useEditorStore((s) => s.connectEdge);
  const edges = useEditorStore((s) => s.edges);
  const { screenToFlowPosition } = useReactFlow();
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close on Esc + on outside click.
  useEffect(() => {
    if (!addMenu.open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeAddMenu();
    }
    function onMouseDown(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) {
        closeAddMenu();
      }
    }
    window.addEventListener("keydown", onKey);
    // Use capture so we beat react-flow's own pane handlers.
    window.addEventListener("mousedown", onMouseDown, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onMouseDown, true);
    };
  }, [addMenu.open, closeAddMenu]);

  const validTypes = useMemo<NodeType[]>(() => {
    if (!addMenu.open) return [];
    if (addMenu.sourceContext && definition) {
      const source = definition.nodes[addMenu.sourceContext.sourceId];
      if (!source) return [];
      return validTargetsForOutput(
        addMenu.sourceContext.sourceId,
        source.type,
        addMenu.sourceContext.sourceHandle,
        edges,
      );
    }
    return [...ADDABLE_NODE_TYPES];
  }, [addMenu, definition, edges]);

  if (!addMenu.open) return null;

  // Group + filter the catalog.
  const grouped = new Map<CategoryKey, CatalogEntry[]>();
  for (const t of validTypes) {
    const entry = CATALOG[t];
    if (!entry) continue;
    if (!grouped.has(entry.category)) grouped.set(entry.category, []);
    grouped.get(entry.category)!.push(entry);
  }

  // Clamp position so the menu stays on-screen.
  const left = Math.min(addMenu.x, window.innerWidth - MENU_WIDTH - 16);
  const top = Math.min(addMenu.y, window.innerHeight - MENU_MAX_HEIGHT - 16);

  function onPick(type: NodeType) {
    let position: { x: number; y: number };
    if (addMenu.sourceContext) {
      // Position the new node directly below the source so the auto-connected
      // edge has somewhere clean to land. A subsequent Arrange click fixes
      // tighter packing if the user wants.
      const source = definition?.nodes[addMenu.sourceContext.sourceId];
      const sourcePos =
        positions[addMenu.sourceContext.sourceId] ?? { x: 200, y: 200 };
      position = source
        ? { x: sourcePos.x, y: sourcePos.y + 200 }
        : sourcePos;
    } else {
      position = screenToFlowPosition({ x: addMenu.x, y: addMenu.y });
    }
    const newId = addNode(type, position);
    if (addMenu.sourceContext && newId) {
      connectEdge(
        addMenu.sourceContext.sourceId,
        addMenu.sourceContext.sourceHandle,
        newId,
      );
    }
    closeAddMenu();
  }

  return (
    <div
      ref={menuRef}
      role="menu"
      style={{
        position: "fixed",
        left,
        top,
        width: MENU_WIDTH,
        maxHeight: MENU_MAX_HEIGHT,
        zIndex: 1000,
      }}
      className="overflow-y-auto rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 shadow-lg text-sm"
    >
      <div className="px-3 py-2 border-b border-neutral-200 dark:border-neutral-800 text-[11px] uppercase tracking-wide text-neutral-500">
        {addMenu.sourceContext ? "Add next node" : "Add node"}
      </div>
      {validTypes.length === 0 ? (
        <div className="px-3 py-3 text-xs text-neutral-500">
          No valid node can follow this output.
        </div>
      ) : (
        <div className="py-1">
          {CATEGORY_ORDER.filter((c) => grouped.has(c)).map((cat) => (
            <div key={cat} className="py-1">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-neutral-500 flex items-center gap-1.5">
                <span
                  className={clsx("w-1.5 h-1.5 rounded-full", CATEGORY_DOT[cat])}
                />
                {CATEGORY_LABEL[cat]}
              </div>
              <ul>
                {grouped.get(cat)!.map((entry) => {
                  const Icon = entry.icon;
                  return (
                    <li key={entry.type}>
                      <button
                        type="button"
                        onClick={() => onPick(entry.type)}
                        className="w-full text-left px-3 py-1.5 flex items-start gap-2 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                      >
                        <Icon className="w-4 h-4 mt-0.5 text-neutral-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium">{entry.label}</div>
                          <div className="text-[11px] text-neutral-500 line-clamp-2">
                            {entry.description}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
